import { useTranslation } from "react-i18next"
import "./Report.css"
import { useState, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getSurveySession } from "../../api/digiscore-api"
import HowToRead from "../../components/HowToRead/HowToRead"
import RessourcesPage from "../Ressources/RessourcesPage"
import Loading from "../../components/Loading/Loading"
import { useAuth0 } from "@auth0/auth0-react"
import { jwtDecode } from "jwt-decode"

const GOMMET_GREEN = encodeURI("/gommetteGreen.svg")
const GOMMET_RED = encodeURI("/gommetteRed.svg")

if (typeof window !== "undefined") {
  try {
    const searchParams = new URLSearchParams(window.location.search)
    const printToken = searchParams.get("printToken")

    if (printToken) {
      localStorage.setItem("auth_token", printToken)
      sessionStorage.setItem("auth_token", printToken)
      window.__puppeteerAuth = true
      console.log("[ReportPage] printToken détecté — mode Puppeteer activé")
    }
  } catch (e) {
    console.warn("[ReportPage] Erreur lecture printToken:", e)
  }

  const t = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token")
  if (t) {
    try {
      const { exp } = JSON.parse(atob(t.split(".")[1]))
      if (Date.now() < exp * 1000) {
        window.__puppeteerAuth = true
      } else {
        localStorage.removeItem("auth_token")
        sessionStorage.removeItem("auth_token")
      }
    } catch (_) {}
  }
}

function normalizeKey(s) {
  return s
    ?.toString()
    ?.trim()
    ?.normalize("NFD")
    ?.replace(/\p{Diacritic}/gu, "")
    ?.replace(/[’']/g, "'")
    ?.replace(/[?!]/g, "")
    ?.replace(/\s+/g, " ")
    ?.toLowerCase();
}

function Report(props) {
  const { t, i18n } = useTranslation()
  const { sessionId } = useParams()
  const navigate = useNavigate()

  const contentRef = useRef()

  const [isLoading, setLoading] = useState(true)
  const [isLoadingPdf] = useState(false)
  const [responses, setResponses] = useState([])
  const [survey, setSurvey] = useState({})
  const [enterprise, setEnterprise] = useState({})

  function getSkippedQuestionsBefore(resps, questionIndex) {
    return resps
      .slice(0, questionIndex)
      .filter(
        q =>
          q.question.hidden ||
          !q.answer ||
          (typeof q.answer !== "string" && q.answer.length < 1)
      ).length
  }

  function isAxisVisible(answer, question, axis) {
    if (answer[axis]) {
      return answer[axis] !== ""
    }
    return false
  }

  function getAnswserAxisClass(answer, question, axis) {
    const axisMeta = answer[axis]
    if ((axisMeta === "1" || axisMeta === "2") && question.answered) return "success"
    if (axisMeta === "0") return "fail"
    return ""
  }

  function getGommetteSrc(answer, question, axis) {
    const cls = getAnswserAxisClass(answer, question, axis)
    if (cls === "success") return GOMMET_GREEN
    if (cls === "fail") return GOMMET_RED
    return undefined
  }

  const { isAuthenticated, loginWithRedirect } = useAuth0()

  useEffect(() => {
    const token =
      localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token")

    if (token && !isAuthenticated) {
      try {
        const decoded = jwtDecode(token)
        const exp = decoded.exp * 1000

        if (Date.now() < exp) {
          console.log("[ReportPage] Token local détecté — mode Puppeteer activé")
          window.__puppeteerAuth = true
        } else {
          console.warn("[ReportPage] Token expiré — suppression storage")
          localStorage.removeItem("auth_token")
          sessionStorage.removeItem("auth_token")
        }
      } catch (e) {
        console.warn("[ReportPage] Erreur décodage token local", e)
      }
    }

    if (!isAuthenticated && !token) {
      loginWithRedirect()
    }
  }, [isAuthenticated, loginWithRedirect])

  useEffect(() => {
    async function fetchData() {
      await i18n.changeLanguage("fr")

      try {
        const session = await getSurveySession(sessionId, props.token)

        if (session) {
          setSurvey(session)
          let survey2020 = null
          let survey2022 = null

          if (session.namespace === "BaaS20E") {
            survey2020 = await fetch("/Form/survey_2020.json").then(r => r.json())
            survey2022 = await fetch("/Form/survey_2022.json").then(r => r.json())
            const byNum = {}
            survey2020.forEach(q => {
              byNum[q.num] = q
            })
            session.__survey2020_byNum = byNum
            session.responses = session.responses.map(r => {
              const qNum = r.question?.num
              const qDef = session.__survey2020_byNum[qNum]

              if (qDef) {
                r.question.responses = Array.isArray(qDef.responses) ? qDef.responses : []
              } else {
                r.question.responses = []
              }

              return r
            })

            const byNum2022 = {}
            survey2022.forEach(q => {
              byNum2022[q.num] = q
            })
            session.__survey2022_byNum = byNum2022
          }

          setResponses(session.responses)
          setEnterprise(session.enterprise)
        }

        setLoading(false)
      } catch (error) {
        console.log(error)
        navigate("/error")
      }
    }

    fetchData()
  }, [sessionId, props.token, i18n, navigate])

  if (!isAuthenticated && !window.__puppeteerAuth) {
    return <Loading />
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <div>
      {isLoadingPdf && <Loading />}

      <div ref={contentRef} className="print-container">
        <div className="page">
          <div className="text-center">
            <img
              src="/logoDigiscore.svg"
              alt="Logo Digiscore"
              className="header__logo"
            />
          </div>

          <br />
          <br />

          <h1 className="text-center">
            {t("report.title")}
            <br />
          </h1>

          <br />
          <br />

          <span className="color-blue subtitle">{enterprise.name}</span>

          <span className="textSubtitle">
            {t("report.etablishedByAdn", {
              year: survey.namespace === "BaaS22E" ? 2022 : 2020
            })}
            <br />
            <br />
            {t("report.surveyCompleted")}
            {t("report.surveyCompletedBy", { name: "" })}
            <span className="textEmail">{survey.user?.email}</span>{" "}
            le {new Date(survey.completedAt).toLocaleDateString()}
          </span>

          <br />
          <br />
          <br />
          <br />
          <br />

          <div className="score-container">
            <table cellPadding="0" cellSpacing="0" className="score-table">
              <tbody>
                <tr>
                  <td style={{ fontSize: "1.2rem" }}>
                    <div
                      style={{
                        height: "48px",
                        lineHeight: "48px",
                        fontWeight: "700",
                        fontSize: "21px"
                      }}
                    >
                      {t("report.globalScore")}
                    </div>
                  </td>
                  <td
                    style={{
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.2rem"
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid #52c3f1",
                        borderRadius: "50%",
                        width: "64px",
                        height: "64px",
                        margin: "0 auto",
                        fontWeight: "700",
                        fontSize: "22px"
                      }}
                    >
                      {survey.globalScore}
                    </div>
                  </td>
                </tr>

                <tr>
                  <td
                    colSpan={2}
                    style={{
                      padding: ".5rem 0",
                      color: "#52c3f1",
                      fontSize: "16px",
                      fontWeight: "600"
                    }}
                  >
                    {t("report.scoreDistribution")} :
                  </td>
                </tr>

                <tr className="distribution-cellTr">
                  <td className="distribution-cell">
                    {survey.namespace === "BaaS20E"
                      ? t("report.infrastructureTitle")
                      : t("result.content.chapter1.li1.title")}
                  </td>
                  <td className="distribution-cell-score">
                    {survey.infrastructure}
                  </td>
                </tr>

                <tr className="distribution-cellTr">
                  <td className="distribution-cell">
                    {survey.namespace === "BaaS20E"
                      ? t("report.humanResourcesTitle")
                      : t("result.content.chapter1.li2.title")}
                  </td>
                  <td className="distribution-cell-score">
                    {survey.humanResources}
                  </td>
                </tr>

                <tr className="distribution-cellTr">
                  <td className="distribution-cell">
                    {survey.namespace === "BaaS20E"
                      ? t("report.processFlowTitle")
                      : t("result.content.chapter1.li3.title")}
                  </td>
                  <td className="distribution-cell-score">
                    {survey.processFlow}
                  </td>
                </tr>

                <tr className="distribution-cellTr">
                  <td className="distribution-cell">
                    {survey.namespace === "BaaS20E"
                      ? t("report.salesProductsTitle")
                      : t("result.content.chapter1.li4.title")}
                  </td>
                  <td className="distribution-cell-score">
                    {survey.salesProducts}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="multipages">
          {survey.namespace === "BaaS20E" ? (
            <>
              <h1>{t("result.content.title")}</h1>
              <br />
              <p>{t("report.howToRead")}</p>
            </>
          ) : (
            <HowToRead />
          )}
        </div>

        <div className="force-new-page">
          <table className="response-table">
            <thead>
              <tr>
                <th className="th-score vertical-header">
                  1 - {t("result.content.chapter1.li1.title")}
                </th>
                <th className="th-score vertical-header">
                  2 - {t("result.content.chapter1.li2.title")}
                </th>
                <th className="th-score vertical-header">
                  3 - {t("result.content.chapter1.li3.title")}
                </th>
                <th className="th-score vertical-header">
                  4 - {t("result.content.chapter1.li4.title")}
                </th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {responses.map((response, questionIndex) => {
                let content = []

                if (
                  response.answer &&
                  (typeof response.answer === "string" ||
                    response.answer.length > 0)
                ) {
                  if (response.question.isStartTheme) {
                    content.push(
                      <tr
                        key={`theme-${response.question.num}`}
                        className="tr-theme"
                      >
                        <td colSpan={5}>
                          <span>{response.question.themeName}</span>
                        </td>
                      </tr>
                    )
                  }

                  if (!response.question.hidden) {
                    content.push(
                      <tr
                        key={`question-${response.question.num}`}
                        className="tr-question"
                      >
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td>
                          <p className="question">
                            {questionIndex +
                              1 -
                              getSkippedQuestionsBefore(
                                responses,
                                questionIndex
                              )}
                            . {response.question.title}
                          </p>
                        </td>
                      </tr>
                    )

                    if (typeof response.answer === "string") {
                      content.push(
                        <tr
                          key={`response-${response.question.num}-answer`}
                        >
                          <td></td>
                          <td></td>
                          <td></td>
                          <td></td>
                          <td className="answered">
                            {response.answer}
                          </td>
                        </tr>
                      )
                    } else {
                      response.question.responses.forEach((choiceLabel, answerIndex) => {
                        // libellé brut de l’option
                        const rawLabel =
                          typeof choiceLabel === "string"
                            ? choiceLabel
                            : choiceLabel?.label || choiceLabel?.value || ""

                        const label = rawLabel

                        // sélection 2020 — version robuste (texte OU code numérique)
                        let isSelected = false

                        if (survey.namespace === "BaaS20E") {
                          const normLabel = normalizeKey(rawLabel)

                          const matchSingleAnswer = ans => {
                            if (ans == null) return false

                            // nombre direct (1, 2, 3, ...)
                            if (typeof ans === "number") {
                              return ans === answerIndex + 1
                            }

                            if (typeof ans === "string") {
                              const normAns = normalizeKey(ans)

                              // 1) correspondance numérique directe
                              const num = Number(ans);
                              if (!Number.isNaN(num) && num === answerIndex + 1) return true;

                              // 2) correspondance texte directe
                              if (normAns && normLabel && normAns === normLabel) return true;

                              // 3) correspondance par comparaison au libellé "n. xxx"
                              const labelNumber = Number(label.split(".")[0]);
                              if (!Number.isNaN(labelNumber) && labelNumber === num) return true;


                              return false
                            }

                            if (typeof ans === "object") {
                              const value = ans.value ?? ans.label

                              if (typeof value === "number") {
                                return value === answerIndex + 1
                              }

                              if (typeof value === "string") {
                                const normValue = normalizeKey(value)

                                if (normValue && normLabel && normValue === normLabel) {
                                  return true
                                }

                                const asNum = Number(value)
                                if (!Number.isNaN(asNum)) {
                                  return asNum === answerIndex + 1
                                }
                              }
                            }

                            return false
                          }

                          if (
                            typeof response.answer === "string" ||
                            typeof response.answer === "number"
                          ) {
                            isSelected = matchSingleAnswer(response.answer)
                          } else if (Array.isArray(response.answer)) {
                            isSelected = response.answer.some(a =>
                              matchSingleAnswer(a)
                            )
                          }
                        }

                        // sélection standard 2022–2025 (inchangée)
                        else {
                          isSelected = !!response.answer[answerIndex]?.answered
                        }

                        content.push(
                          <tr
                            key={`response-${response.question.num}-${answerIndex}`}
                          >
                            <td className="td-score">
                              {isAxisVisible(
                                response.answer?.[answerIndex] || {},
                                response.question,
                                "infrastructure"
                              ) ? (
                                <div
                                  className={`axis-scoring ${getAnswserAxisClass(
                                    response.answer?.[answerIndex] || {},
                                    response.question,
                                    "infrastructure"
                                  )}`}
                                >
                                  <img
                                    src={getGommetteSrc(
                                      response.answer?.[answerIndex] || {},
                                      response.question,
                                      "infrastructure"
                                    )}
                                    alt='Gommette'
                                    className="gomet-icon"
                                  />
                                </div>
                              ) : null}
                            </td>

                            <td className="td-score">
                              {isAxisVisible(
                                response.answer?.[answerIndex] || {},
                                response.question,
                                "humanRessource"
                              ) ? (
                                <div
                                  className={`axis-scoring ${getAnswserAxisClass(
                                    response.answer?.[answerIndex] || {},
                                    response.question,
                                    "humanRessource"
                                  )}`}
                                >
                                  <img
                                    src={getGommetteSrc(
                                      response.answer?.[answerIndex] || {},
                                      response.question,
                                      "humanRessource"
                                    )}
                                    alt='Gommette'
                                    className="gomet-icon"
                                  />
                                </div>
                              ) : null}
                            </td>

                            <td className="td-score">
                              {isAxisVisible(
                                response.answer?.[answerIndex] || {},
                                response.question,
                                "processFlow"
                              ) ? (
                                <div
                                  className={`axis-scoring ${getAnswserAxisClass(
                                    response.answer?.[answerIndex] || {},
                                    response.question,
                                    "processFlow"
                                  )}`}
                                >
                                  <img
                                    src={getGommetteSrc(
                                      response.answer?.[answerIndex] || {},
                                      response.question,
                                      "processFlow"
                                    )}
                                    alt='Gommette'
                                    className="gomet-icon"
                                  />
                                </div>
                              ) : null}
                            </td>

                            <td className="td-score">
                              {isAxisVisible(
                                response.answer?.[answerIndex] || {},
                                response.question,
                                "strategy"
                              ) ? (
                                <div
                                  className={`axis-scoring ${getAnswserAxisClass(
                                    response.answer?.[answerIndex] || {},
                                    response.question,
                                    "strategy"
                                  )}`}
                                >
                                  <img
                                    src={getGommetteSrc(
                                      response.answer?.[answerIndex] || {},
                                      response.question,
                                      "strategy"
                                    )}
                                    alt='Gommette'
                                    className="gomet-icon"
                                  />
                                </div>
                              ) : null}
                            </td>

                            <td
                              className={`td-answer-label ${
                                isSelected ? "answered" : ""
                              }`}
                            >
                              {`${answerIndex + 1}. ${label}`}
                              {isSelected &&
                              response.answer?.[answerIndex]?.description ? (
                                <>
                                  <br />
                                  <small>
                                    {response.answer?.[answerIndex].description}
                                  </small>
                                </>
                              ) : null}
                            </td>
                          </tr>
                        )
                      })
                    }
                  }
                }

                return content || undefined
              })}
            </tbody>
          </table>
        </div>

        <div className="multipage">
          <RessourcesPage />
        </div>
      </div>
    </div>
  )
}

export default Report
